"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogProps as AriaDialogProps,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
	ModalOverlay as AriaModalOverlay,
	ModalOverlayProps as AriaModalOverlayProps,
	composeRenderProps,
} from "react-aria-components";

import { cn } from "@/utils/cn";

const sheetVariants = cva(
	[
		"fixed z-[10002] gap-4 bg-background shadow-lg",
		/* Simplified animations for better performance */
		"data-[entering]:duration-200 data-[entering]:ease-out data-[entering]:animate-in",
		"data-[exiting]:duration-150 data-[exiting]:ease-in data-[exiting]:animate-out",
	],
	{
		variants: {
			side: {
				top: "inset-x-0 top-0 border-b data-[entering]:slide-in-from-top data-[exiting]:slide-out-to-top",
				bottom: "inset-x-0 bottom-0 border-t data-[entering]:slide-in-from-bottom data-[exiting]:slide-out-to-bottom",
				left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm data-[entering]:slide-in-from-left data-[exiting]:slide-out-to-left",
				right:
					"inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm data-[entering]:slide-in-from-right data-[exiting]:slide-out-to-right",
			},
		},
		defaultVariants: { side: "right" },
	},
);

const Sheet = AriaDialogTrigger;

const SheetTrigger = React.forwardRef<
	React.ComponentRef<typeof AriaButton>,
	React.ComponentPropsWithoutRef<typeof AriaButton>
>(({ ...props }, ref) => <AriaButton ref={ref} {...props} />);

SheetTrigger.displayName = "SheetTrigger";

const SheetOverlay = ({ className, isDismissable = true, ...props }: AriaModalOverlayProps) => (
	<AriaModalOverlay
		isDismissable={isDismissable}
		className={composeRenderProps(className, className =>
			cn(
				"fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm",
				/* Simplified overlay animations */
				"data-[entering]:duration-200 data-[entering]:ease-out data-[entering]:animate-in data-[entering]:fade-in-0",
				"data-[exiting]:duration-150 data-[exiting]:ease-in data-[exiting]:animate-out data-[exiting]:fade-out-0",
				className,
			),
		)}
		{...props}
	/>
);

interface SheetContentProps
	extends Omit<React.ComponentProps<typeof AriaModal>, "children">, VariantProps<typeof sheetVariants> {
	children?: AriaDialogProps["children"];
	role?: AriaDialogProps["role"];
	closeButton?: boolean;
}

const SheetContent = ({
	className,
	children,
	side = "right",
	role,
	closeButton = true,
	...props
}: SheetContentProps) => (
	<AriaModal
		className={composeRenderProps(className, className =>
			cn(sheetVariants({ side }), "h-full flex flex-col", className),
		)}
		{...props}
	>
		<AriaDialog role={role} className="h-full outline-none flex flex-col">
			{composeRenderProps(children, (children, renderProps) => (
				<>
					{children}
					{closeButton && (
						<AriaButton
							onPress={renderProps.close}
							className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[disabled]:pointer-events-none data-[entering]:bg-accent data-[entering]:text-muted-foreground data-[hovered]:opacity-100 data-[focused]:outline-none data-[focused]:ring-2 data-[focused]:ring-ring data-[focused]:ring-offset-2"
						>
							<X className="size-4" />
							<span className="sr-only">Close</span>
						</AriaButton>
					)}
				</>
			))}
		</AriaDialog>
	</AriaModal>
);

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex flex-col gap-1.5 p-4", className)} {...props} />
);

// const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
// 	<div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />
// );

// const SheetTitle = ({ className, ...props }: AriaHeadingProps) => (
// 	<AriaHeading slot="title" className={cn("text-foreground font-semibold", className)} {...props} />
// );

// const SheetDescription = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
// 	<p className={cn("text-muted-foreground text-sm", className)} {...props} />
// );

const SheetClose = React.forwardRef<
	React.ElementRef<typeof AriaButton>,
	React.ComponentPropsWithoutRef<typeof AriaButton>
>(({ ...props }, ref) => <AriaButton ref={ref} {...props} />);

SheetClose.displayName = "SheetClose";

export { Sheet, SheetContent, SheetHeader, SheetOverlay, SheetTrigger };
